/**
 * Main Control. An organisation-wide cockpit, ordered by operational urgency:
 * confirmed failures, then rising risk, then decisions, then everything else.
 */
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useProjection } from '../hooks/useProjection';
import { useLive } from '../hooks/useLive';
import { Intervention } from '../components/Intervention';
import { BridgeRoom } from '../components/BridgeRoom';
import { CrewTile } from '../components/CrewTile';
import { FreshnessTag, Panel, Stat, StateChip, ToneChip, Unknown } from '../components/Primitives';
import {
  DegradedBanner,
  EmptyState,
  ErrorState,
  LiveBadge,
  SkeletonPanel,
  SkeletonTiles,
} from '../components/States';
import { clockTime, duration, relativeTime } from '../lib/format';
import { toneFor } from '../lib/tone';
import { useIsPhone } from '../hooks/useMediaQuery';
import { CompactCrewTile, CompactProjectTile } from '../components/Compact';
import { Collapsible } from '../components/Collapsible';
import { demoPortfolio } from '../demo/portfolio';

/**
 * `source` has exactly two values and one caller each. `live` asks the server
 * for the portfolio projection, as everything else does. `demo` builds one in
 * the browser and makes no request at all — no fetch, no stream — which is the
 * property that makes demo mode incapable of touching the live bridge rather
 * than merely unlikely to.
 */
export function MainControl({ source = 'live' }: { source?: 'live' | 'demo' } = {}) {
  const demo = source === 'demo';
  const projection = useProjection(
    demo ? () => Promise.resolve(demoPortfolio()) : () => api.portfolio(),
    [demo],
  );
  const live = useLive(
    ['portfolio.updated', 'incident.updated', 'capacity.updated'],
    projection.reload,
    !demo,
  );
  const isPhone = useIsPhone();

  if (projection.loading) {
    return (
      <>
        <SkeletonPanel rows={2} />
        <SkeletonTiles />
      </>
    );
  }
  if (projection.error) return <ErrorState error={projection.error} onRetry={projection.reload} />;
  const view = projection.data!;

  const critical = view.interventions.filter(
    (i) => i.severity === 'CRITICAL' || i.severity === 'BLOCKER',
  );
  const rising = view.interventions.filter((i) => i.severity === 'RISK');
  const attention = view.interventions.filter(
    (i) => i.severity === 'ATTENTION' || i.severity === 'INFO',
  );
  const fleetTone =
    view.fleetHealth.status === 'INCIDENT'
      ? 'critical'
      : view.fleetHealth.status === 'DEGRADED'
        ? 'attention'
        : 'success';

  return (
    <>
      <header className="topstrip">
        <div>
          <div className="label">{view.tenant.name} · AI development operations</div>
          <h1 className="topstrip__title">Main Control</h1>
        </div>
        <div className="topstrip__meta">
          <ToneChip tone={fleetTone}>
            {view.fleetHealth.status === 'OPERATIONAL'
              ? 'Systems operational'
              : view.fleetHealth.status === 'DEGRADED'
                ? 'Degraded sources'
                : 'Incident open'}
          </ToneChip>
          {demo ? (
            <ToneChip tone="attention">Simulated · no live stream</ToneChip>
          ) : (
            <LiveBadge connection={live.connection} lastEventAt={live.lastEventAt} />
          )}
          <span className="freshness">{clockTime(view.generatedAt)}</span>
        </div>
      </header>

      {projection.degraded && (
        <DegradedBanner onRetry={projection.reload} loadedAt={projection.loadedAt} />
      )}
      {/* The bridge itself, at every width. A phone gets the same table with
          the same chairs — the picture is the one thing the list below cannot
          say — and the consoles there shed the readouts the list repeats. */}
      <BridgeRoom view={view} compact={isPhone} />

      {/* Intervention strip: what a person has to do, before anything else. */}
      <section aria-labelledby="interventions-heading" className="stack">
        <div className="spread">
          <h2 id="interventions-heading" className="panel__title">
            Needs intervention
          </h2>
          <span className="small dim">
            {view.interventions.length} open · {critical.length} confirmed failure
            {critical.length === 1 ? '' : 's'}
          </span>
        </div>

        {view.interventions.length === 0 ? (
          <EmptyState
            tone="success"
            title="Nothing needs a person right now"
            body="No confirmed failure, blockade, overdue decision, lease exception or degraded connection is open across the portfolio. This reflects sources that are currently fresh."
          />
        ) : (
          <div className="stack stack--tight">
            {[...critical, ...rising, ...attention].map((item) => (
              <Intervention key={item.id} item={item} />
            ))}
          </div>
        )}
      </section>

      {/* On a phone, and whenever there is nothing to put on the console, the
          projects stay a plain list. The console never becomes the only way
          to reach a project. */}
      {(isPhone || view.projects.length === 0) && (
        <section aria-labelledby="projects-heading" className="stack">
          <div className="spread">
            <h2 id="projects-heading" className="panel__title">
              Projects in flight
            </h2>
            <span className="small dim">
              Sorted by operational urgency, then critical-path impact.
            </span>
          </div>
          {view.projects.length === 0 ? (
            <EmptyState
              title="No projects yet"
              body="Connect an existing repository or bootstrap a new project to start observing it."
              action={
                <Link className="btn btn--primary" to="/integrations/connect">
                  Connect a project
                </Link>
              }
            />
          ) : (
            <div className="stack">
              {view.projects.map((tile) => (
                <CompactProjectTile key={tile.id} tile={tile} />
              ))}
            </div>
          )}
        </section>
      )}

      <section aria-labelledby="crew-heading" className="stack">
        <div className="spread">
          <h2 id="crew-heading" className="panel__title">
            Colleagues and licensed seats
          </h2>
          <Link className="btn btn--sm btn--quiet" to="/crew">
            Crew &amp; capacity
          </Link>
        </div>
        <p className="small dim">
          These are people and the seats assigned to them. AI workers live in Agent Operations and
          are never merged with a colleague.
        </p>
        {isPhone ? (
          (() => {
            // On a phone, lead with the colleagues who need something from a
            // person; the rest stay one tap away rather than filling the screen.
            // Spare capacity on an authorised seat is surfaced alongside the
            // people who are stopped: together they are the handoff decision.
            const needsAttention = view.crew.filter(
              (member) =>
                member.status === 'BLOCKED_LIMIT' ||
                member.status === 'WAITING' ||
                member.status === 'BLOCKED_OTHER' ||
                member.status === 'IDLE_CAPACITY',
            );
            const rest = view.crew.filter((member) => !needsAttention.includes(member));
            return (
              <div className="stack">
                {needsAttention.map((member) => (
                  <CompactCrewTile key={member.id} member={member} />
                ))}
                {rest.length > 0 && (
                  <details className="panel">
                    <summary
                      className="panel__title"
                      style={{
                        cursor: 'pointer',
                        minHeight: 44,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {rest.length} more colleague{rest.length === 1 ? '' : 's'}
                    </summary>
                    <div className="stack" style={{ marginTop: 12 }}>
                      {rest.map((member) => (
                        <CompactCrewTile key={member.id} member={member} />
                      ))}
                    </div>
                  </details>
                )}
              </div>
            );
          })()
        ) : (
          <div className="grid grid--crew">
            {view.crew.map((member) => (
              <CrewTile key={member.id} member={member} />
            ))}
          </div>
        )}
      </section>

      <Collapsible summary="Fleet trajectory and team range">
        <div className="grid grid--sidebar">
          <Panel
            title="Fleet trajectory"
            subtitle={`Completed journeys and accepted slices over the last ${view.fleet.windowHours} hours, compared with capacity burned.`}
            actions={<FreshnessTag freshness={view.fleet.freshness} />}
          >
            <div className="grid grid--4">
              <Stat
                label="Completed journeys"
                value={view.fleet.completedJourneys}
                note={`${view.fleet.completedJourneysPerDay ?? 0}/day`}
              />
              <Stat
                label="Accepted slices"
                value={view.fleet.acceptedSlices}
                note={`${view.fleet.acceptedSlicesPerDay ?? 0}/day`}
              />
              <Stat
                label="Capacity burned"
                value={view.fleet.burnPercent === null ? <Unknown /> : `${view.fleet.burnPercent}%`}
                note="weekly fuel across the fleet"
              />
              <Stat
                label="Active workers"
                value={view.fleet.activeWorkers}
                note="a control input, not a score"
              />
            </div>

            <div className="divider" />

            {/* Burn against throughput is the whole point: more agents is not speed. */}
            <div className="grid grid--3">
              <Stat
                label="Burn per accepted slice"
                value={
                  view.fleet.burnPerAcceptedSlice === null ? (
                    <Unknown />
                  ) : (
                    `${view.fleet.burnPerAcceptedSlice}%`
                  )
                }
                note="lower is better"
              />
              <Stat
                label="Burn per completed journey"
                value={
                  view.fleet.burnPerCompletedJourney === null ? (
                    <Unknown />
                  ) : (
                    `${view.fleet.burnPerCompletedJourney}%`
                  )
                }
                note="the primary KPI"
              />
              <Stat
                label="Throughput trend"
                value={view.fleet.throughputTrend}
                tone={
                  view.fleet.throughputTrend === 'UP'
                    ? 'success'
                    : view.fleet.throughputTrend === 'DOWN'
                      ? 'risk'
                      : 'neutral'
                }
                note="versus the previous window"
              />
            </div>

            <div className="divider" />

            <div className="grid grid--3 small">
              <Stat label="Blocked minutes" value={view.fleet.blockedMinutes} unit="m" />
              <Stat label="Waiting minutes" value={view.fleet.waitingMinutes} unit="m" />
              <Stat label="Rework minutes" value={view.fleet.reworkMinutes} unit="m" />
            </div>
          </Panel>

          <Panel title="Team range" subtitle="Only authorised, currently usable seats are counted.">
            <div className="stack">
              <div className="spread">
                <StateChip kind="capacity" state={view.teamRange.state} size="lg" />
                <span className="mono">
                  {view.teamRange.totalAgentHours === null ? (
                    <Unknown />
                  ) : (
                    `${view.teamRange.totalAgentHours} agent-h`
                  )}
                </span>
              </div>
              <p className="small dim">
                {view.teamRange.requiredAgentHours === null
                  ? 'No comparable workload estimate is available.'
                  : `About ${duration(view.teamRange.requiredAgentHours)} of work is estimated across the portfolio.`}
              </p>
              <div>
                <div className="label">Eligible now</div>
                <p className="small">
                  {view.teamRange.eligibleMembers.length === 0
                    ? 'Nobody can start work right now.'
                    : view.teamRange.eligibleMembers.map((m) => m.name).join(', ')}
                </p>
              </div>
              {view.teamRange.excluded.length > 0 && (
                <div>
                  <div className="label">Not counted</div>
                  <ul className="stack stack--tight small" style={{ margin: 0, paddingLeft: 16 }}>
                    {view.teamRange.excluded.map((entry) => (
                      <li key={entry.memberId}>
                        <strong>{entry.name}</strong> — {entry.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </Panel>
        </div>
      </Collapsible>

      <Collapsible summary="Capacity balance and recent activity">
        <div className="grid grid--sidebar">
          <Panel
            title="Capacity balance"
            subtitle="Where capacity is short while it sits unused elsewhere."
            tone={toneFor('severity', view.imbalance.severity)}
          >
            <div className="stack">
              <div className="inline">
                <StateChip kind="severity" state={view.imbalance.severity} />
                <strong>{view.imbalance.headline}</strong>
              </div>
              <p className="small">{view.imbalance.detail}</p>
              {view.imbalance.recommendation && (
                <p className="small dim">{view.imbalance.recommendation}</p>
              )}
              {view.imbalance.recommendedHandoff && (
                <div className="card card--toned" data-tone="success">
                  <div className="label">Recommended handoff</div>
                  <p className="small" style={{ marginTop: 4 }}>
                    <strong>{view.imbalance.recommendedHandoff.fromName}</strong> →{' '}
                    <strong>{view.imbalance.recommendedHandoff.toName}</strong> on{' '}
                    {view.imbalance.recommendedHandoff.projectName} ·{' '}
                    {view.imbalance.recommendedHandoff.workstream}
                  </p>
                  <ul
                    className="stack stack--tight tiny dim"
                    style={{ margin: '8px 0 0', paddingLeft: 16 }}
                  >
                    {view.imbalance.recommendedHandoff.rationale.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                  <Link className="btn btn--sm" to="/crew" style={{ marginTop: 12 }}>
                    Review the handoff
                  </Link>
                </div>
              )}
            </div>
          </Panel>

          <Panel title="Recent activity" subtitle="Cross-project events, newest first.">
            {view.activity.length === 0 ? (
              <EmptyState
                title="No events recorded yet"
                body="Connected sources have not delivered any events."
              />
            ) : (
              <ul
                className="stack stack--tight"
                style={{ listStyle: 'none', margin: 0, padding: 0 }}
              >
                {view.activity.slice(0, 12).map((item) => (
                  <li key={item.id} className="spread small" data-tone={item.tone}>
                    <span className="truncate">
                      <span className="chip__glyph tone-text" aria-hidden="true">
                        ·
                      </span>{' '}
                      {item.summary}
                    </span>
                    <span className="tiny dim" style={{ whiteSpace: 'nowrap' }}>
                      {item.projectName ?? ''} · {relativeTime(item.at)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </Collapsible>
    </>
  );
}
